// =============================================================================
// components/glass/GlassSurface.tsx — internal base for all glass primitives.
//
// Renders one calibrated glass layer: translucent surface + low-alpha border +
// top inner sheen (fake light edge) + tuned ambient shadow, with an optional
// press spring (scale + brightness dip). The public primitives (GlassCard/Bar/
// Sheet/Pill/Button) are thin wrappers over this — one implementation, themed
// entirely by tokens.ts. Do not re-implement glass anywhere else.
//
// BARE-RN MIGRATION (blueprint §1, swap #3): the sheen gradient was
// `expo-linear-gradient` → now `react-native-linear-gradient`. Same
// colors/start/end/style props — a pure import swap. No expo-blur was used (the
// glass is JS-only translucency: surface + sheen + border + shadow), so nothing
// to drop there.
// =============================================================================
import React, { useRef } from 'react';
import {
  Animated, Pressable, View, StyleSheet, ViewStyle, StyleProp,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { glassTokens, glassColorTokens, surfaceFor, GlassVariant } from './tokens';

export interface GlassSurfaceProps {
  variant?: GlassVariant;
  radius?: number;
  sheen?: boolean;            // top highlight (default true)
  shadow?: boolean;           // ambient shadow (default true)
  /** Theme mode. 'light' (lavender, default) or 'dark' (voice/call). Additive. */
  mode?: 'light' | 'dark';
  onPress?: () => void;
  onLongPress?: () => void;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;       // outer (margins/size)
  contentStyle?: StyleProp<ViewStyle>; // inner (padding/layout)
  surfaceColor?: string;      // override fill (e.g. active pill)
  borderColor?: string;
  children?: React.ReactNode;
  accessibilityLabel?: string;
  accessibilityRole?: any;
  hitSlop?: number;
}

export default function GlassSurface({
  variant = 'card',
  radius = glassTokens.radius.lg,
  sheen = true,
  shadow = true,
  mode = 'light',
  onPress,
  onLongPress,
  disabled = false,
  style,
  contentStyle,
  surfaceColor,
  borderColor,
  children,
  accessibilityLabel,
  accessibilityRole,
  hitSlop,
}: GlassSurfaceProps) {
  const scale = useRef(new Animated.Value(1)).current;
  const dim = useRef(new Animated.Value(0)).current; // 0 rest → 1 pressed

  const pressIn = () => {
    Animated.spring(scale, { toValue: glassTokens.pressScale, useNativeDriver: true, speed: 50, bounciness: 0 }).start();
    Animated.timing(dim, { toValue: 1, duration: 90, useNativeDriver: true }).start();
  };
  const pressOut = () => {
    Animated.spring(scale, { toValue: 1, useNativeDriver: true, speed: 40, bounciness: 6 }).start();
    Animated.timing(dim, { toValue: 0, duration: 140, useNativeDriver: true }).start();
  };

  const dark = mode === 'dark';
  const fill = surfaceColor ?? surfaceFor(variant, mode);
  const edge = borderColor ?? (dark ? glassColorTokens.dark.border : glassTokens.border);
  const sheenColors: [string, string] = dark
    ? [glassColorTokens.dark.highlightTop, glassColorTokens.dark.highlightTo]
    : [glassTokens.highlightTop, glassTokens.highlightTo];

  const inner = (
    <View
      style={[
        styles.surface,
        { backgroundColor: fill, borderColor: edge, borderRadius: radius },
        contentStyle,
      ]}
    >
      {sheen && (
        <LinearGradient
          colors={sheenColors}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={[styles.sheen, { borderTopLeftRadius: radius, borderTopRightRadius: radius }]}
          pointerEvents="none"
        />
      )}
      {/* press brightness dip */}
      <Animated.View
        pointerEvents="none"
        style={[styles.dim, { borderRadius: radius, opacity: dim.interpolate({ inputRange: [0, 1], outputRange: [0, 0.08] }) }]}
      />
      {children}
    </View>
  );

  const shadowStyle = shadow ? {
    shadowColor: glassTokens.shadow.color,
    shadowOffset: glassTokens.shadow.offset,
    shadowOpacity: glassTokens.shadow.opacity,
    shadowRadius: glassTokens.shadow.radius,
    elevation: glassTokens.shadow.elevation,
  } : null;

  if (!onPress && !onLongPress) {
    return <View style={[shadowStyle, disabled && { opacity: glassTokens.disabledOpacity }, style]}>{inner}</View>;
  }

  return (
    <Animated.View style={[shadowStyle, { transform: [{ scale }] }, disabled && { opacity: glassTokens.disabledOpacity }, style]}>
      <Pressable
        onPress={disabled ? undefined : onPress}
        onLongPress={disabled ? undefined : onLongPress}
        onPressIn={disabled ? undefined : pressIn}
        onPressOut={disabled ? undefined : pressOut}
        disabled={disabled}
        accessibilityRole={accessibilityRole ?? 'button'}
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ disabled }}
        hitSlop={hitSlop}
        style={styles.pressable}
      >
        {inner}
      </Pressable>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  pressable: { borderRadius: 9999 },
  surface: {
    borderWidth: 1,
    overflow: 'hidden',
  },
  sheen: {
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 44,
  },
  dim: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: '#1A1A2E',
  },
});
